import z from "zod";
import TAB_ICONS from "@/components/icons/tabs";

export enum TileActionType {
  button = "button",
  redirectLink = "redirect_link",
}

export enum TileImageType {
  illustration = "illustration",
  image = "image",
}

export enum BlockLayout {
  oneColumn = "1-column",
  twoColumn = "2-columns",
}

export enum BlockType {
  illustration = "illustration",
  image = "image",
  text = "text",
  button = "button",
  tiles = "tiles",
}

export enum BlockPosition {
  left = "left",
  center = "center",
  right = "right",
}

export enum TabType {
  welcomePage = "welcome_page",
  workflow = "workflow",
  profile = "profile",
  documents = "documents",
  faq = "faq",
  custom = "custom",
  logOut = "log_out",
}

export enum TabPosition {
  top = "top",
  bottom = "bottom",
}

export enum TabVisibility {
  always = "always",
  authenticated = "authenticated",
  unauthenticated = "unauthenticated",
}

export enum WelcomePageType {
  Login = "login",
  AiAgent = "ai_agent",
}

export enum ImageDimensionsMode {
  Scale = "scale",
  FullWidth = "full_width",
}

const imageDimensionsSchema = z.object({
  mode: z.nativeEnum(ImageDimensionsMode),
  scale: z.number().optional(),
});

const tileSchema = z.object({
  title: z.string(),
  description: z.string().nullable(),
  action: z.object({
    type: z.nativeEnum(TileActionType),
    text: z.string().nullable(),
    url: z.string().nullable(),
  }),
  image: z.object({
    type: z.nativeEnum(TileImageType),
    icon: z.string().nullable().optional(),
    file_id: z.number().optional(),
    dimensions: imageDimensionsSchema,
  }),
});

const blockSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(BlockType),
  title: z.string().optional(),
  description: z.string().optional(),
  align: z.nativeEnum(BlockPosition).optional(),
  text: z.string().optional(),
  url: z.string().optional(),
  file_id: z.number().optional(),
  icon: z.string().optional(),
  tiles: z.array(tileSchema).optional(),
  layout: z.nativeEnum(BlockLayout).optional(),
  dimensions: imageDimensionsSchema.optional(),
});

const tabSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(TabType),
  title: z.string(),
  icon: z
    .enum(Object.keys(TAB_ICONS) as [keyof typeof TAB_ICONS, ...Array<keyof typeof TAB_ICONS>])
    .nullable(),
  navigation_position: z.nativeEnum(TabPosition),
  visibility: z.nativeEnum(TabVisibility),
  blocks: z.array(blockSchema).optional(),
  workflow_template_ids: z.array(z.string()).optional(),
  is_fallback: z.boolean().optional(),

  // Config for `welcome_page` type
  mode: z.nativeEnum(WelcomePageType).optional(),
  agent_id: z.string().optional(),
  show_header: z.boolean().optional(),
  show_login_button: z.boolean().optional(),
  start_message_title: z.string().nullable().optional(),
  start_message_text: z.string().nullable().optional(),
});

export const borrowerPlatformTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  content: z.object({
    version: z.string(),
    tabs: z.array(tabSchema),
  }),
  is_active: z.boolean(),
  is_global: z.boolean(),
  is_fallback: z.boolean(),
  is_lendflow_template: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type BorrowerPlatformTemplate = z.infer<typeof borrowerPlatformTemplateSchema>;
export type Tab = z.infer<typeof tabSchema>;
export type Block = z.infer<typeof blockSchema>;
export type Tile = z.infer<typeof tileSchema>;
export type ImageDimensions = z.infer<typeof imageDimensionsSchema>;
